// The test map tab: the folder's tests by project and class, and for the one picked, every file,
// member and EF Core entity reached from the test method and what runs with it, and the types it
// replaces with fakes.
import { testReach, type CodeMap, type CodeTest } from '@modelviewer/core';
import { Box, FlaskConical, Loader2, Search, Shuffle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Canvas, Divider, ToolButton, type CanvasSelection, type CanvasTarget, type Vocabulary } from '../diagram/Canvas';
import { useCardFonts } from '../diagram/DiagramView';
import { measureText } from '../diagram/measure';
import { layoutInput, useLayout } from '../diagram/useLayout';
import { cn } from '../lib/cn';
import { loadCodeMap, selectCode, showTest, useCodeMap } from '../state/codemap';
import { useWorkspace } from '../state/workspace';
import { codeIndex } from './codeIndex';
import { useCodeSettings } from './settings';
import { buildTestGraph, TEST_CARD, typeName } from './testGraph';

const MODES: Vocabulary['modes'] = [
  { value: 'all', label: 'Members', title: 'Every member the test reaches' },
  { value: 'none', label: 'Types', title: 'Only the types with something reached' },
];

/** The test the map shows: the one picked, or the first. */
export function useCurrentTest(): CodeTest | null {
  const tests = useCodeMap((s) => s.map?.tests);
  const picked = useCodeMap((s) => s.test);
  return tests?.find((t) => t.id === picked) ?? tests?.[0] ?? null;
}

export interface TestGroup {
  /** The project's name, or "No project". */
  project: string;
  classes: { type: string; tests: CodeTest[] }[];
}

/** Tests by project, then by class, in the order they are written. */
export function testGroups(map: CodeMap, tests: CodeTest[]): TestGroup[] {
  const names = new Map(map.projects.map((p) => [p.folder, p.name]));
  const projectOf = new Map(map.files.map((f) => [f.path, f.project]));
  const out: TestGroup[] = [];
  for (const t of tests) {
    const folder = projectOf.get(t.file) ?? null;
    const project = folder === null ? 'No project' : (names.get(folder) ?? folder);
    let group = out.find((g) => g.project === project);
    if (!group) out.push((group = { project, classes: [] }));
    let cls = group.classes.find((c) => c.type === t.type);
    if (!cls) group.classes.push((cls = { type: t.type, tests: [] }));
    cls.tests.push(t);
  }
  return out;
}

function TestList({ map, current }: { map: CodeMap; current: CodeTest | null }) {
  const [filter, setFilter] = useState('');
  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return testGroups(map, q ? map.tests.filter((t) => t.id.toLowerCase().includes(q)) : map.tests);
  }, [map, filter]);
  return (
    <aside className="flex w-72 flex-none flex-col border-r border-border bg-panel @max-[44rem]:hidden" aria-label="Tests">
      <div className="flex h-8 items-center gap-1.5 px-3 text-[11px] font-semibold tracking-wider text-faint uppercase">
        Tests <span className="ml-auto font-normal tracking-normal normal-case">{map.tests.length}</span>
      </div>
      <label className="mx-2 mb-1 flex items-center gap-1.5 rounded-md border border-border px-2">
        <Search size={12} className="text-faint" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter tests"
          aria-label="Filter tests"
          className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
        />
      </label>
      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2" role="listbox" aria-label="Test">
        {groups.map((g) => (
          <div key={g.project} role="group" aria-label={g.project}>
            <div role="presentation" className="truncate px-2 pt-2 pb-0.5 text-[11px] font-semibold text-faint">
              {g.project}
            </div>
            {g.classes.map((c) => (
              <div key={c.type} role="group" aria-label={typeName(c.type)}>
                <div role="presentation" className="flex items-center gap-1.5 px-2 py-0.5 text-[12px] text-muted" title={c.type}>
                  <Box size={12} className="flex-none text-faint" />
                  <span className="min-w-0 truncate">{typeName(c.type)}</span>
                </div>
                {c.tests.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="option"
                    aria-selected={current?.id === t.id}
                    onClick={() => showTest(t.id)}
                    title={`${t.id}\n${t.file}:${t.line}`}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-md py-1 pr-2 pl-6 text-left pointer-coarse:py-2',
                      current?.id === t.id ? 'bg-active text-text' : 'text-muted hover:bg-hover hover:text-text',
                    )}
                  >
                    <span className={cn('min-w-0 flex-1 text-[12px] leading-snug [overflow-wrap:anywhere]', t.skipped && 'line-through decoration-faint')}>{t.name}</span>
                    {t.cases > 0 && (
                      <span className="flex-none rounded bg-active px-1 text-[10.5px] leading-4 text-faint" title={`${t.cases} ${t.cases === 1 ? 'case' : 'cases'}`}>
                        ×{t.cases}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </aside>
  );
}

export function TestMapView() {
  const map = useCodeMap((s) => s.map);
  const loading = useCodeMap((s) => s.loading);
  const error = useCodeMap((s) => s.error);
  const source = useWorkspace((s) => s.source);
  const settings = useCodeSettings();
  const fontsReady = useCardFonts();
  const current = useCurrentTest();

  useEffect(() => {
    if (!useCodeMap.getState().map && !useCodeMap.getState().loading) void loadCodeMap();
  }, [source]);

  const reach = useMemo(() => (map && current ? testReach(map, current.id, { guesses: settings.guesses }) : null), [map, current, settings.guesses]);
  const graph = useMemo(() => (map && reach && fontsReady ? buildTestGraph(map, reach, { members: settings.testMembers, measure: measureText }) : null), [map, reach, fontsReady, settings.testMembers]);
  const input = useMemo(() => (graph ? layoutInput(graph) : null), [graph]);
  const layoutKey = source && current ? `${source.id}|test|${current.id}|${settings.testMembers}` : null;
  const { positions, save, relayout } = useLayout(layoutKey, input);

  const selection = useCodeMap((s) => s.selection);
  const selected = useMemo<CanvasSelection>(() => {
    if (!selection || !map) return { node: null, row: null, edge: null };
    if (selection.kind === 'file') return { node: selection.path, row: null, edge: null };
    if (selection.kind === 'edge') return { node: null, row: null, edge: selection.id };
    const file = codeIndex(map).fileOf(selection.id);
    return { node: null, row: file ? `${file}::${selection.id}` : null, edge: null };
  }, [selection, map]);

  const onSelect = useCallback((target: CanvasTarget | null) => {
    if (!target) selectCode(null);
    else if (target.kind === 'node') selectCode(target.node.id === TEST_CARD ? null : { kind: 'file', path: target.node.key });
    else if (target.kind === 'row') selectCode(target.row.id.startsWith('data:') || target.row.id.startsWith('fake:') ? null : { kind: 'member', id: target.row.id });
    else selectCode(target.edge.id === 'test' || target.edge.id.startsWith('setup:') ? null : { kind: 'edge', id: target.edge.id });
  }, []);

  const request = useCodeMap((s) => s.reveal);
  const reveal = useMemo(() => (request?.view === 'tests' ? { id: request.path, nonce: request.nonce, focus: request.focus } : null), [request]);
  const vocabulary = useMemo<Vocabulary>(
    () => ({ search: 'Find file or member', modesLabel: 'What to show', modes: MODES, mode: settings.testMembers, setMode: settings.setTestMembers, joins: false }),
    [settings.testMembers, settings.setTestMembers],
  );

  const tests = map?.tests ?? [];
  const groups = useMemo(() => (map ? testGroups(map, map.tests) : []), [map]);
  let body: React.ReactNode;
  if (error) body = <div className="flex h-full items-center justify-center p-6 text-center text-muted">{error}</div>;
  else if (map && !tests.length) {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-muted">
        <p>No tests found in this folder.</p>
        <p className="max-w-md text-[12px] text-faint">The test map looks for methods marked [Fact] or [Theory] (xunit), [Test] or [TestCase] (NUnit, TUnit) and [TestMethod] (MSTest).</p>
      </div>
    );
  } else if (!graph || !positions || !reach || !current) {
    body = (
      <div className="flex h-full items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" />
        {loading ? `Reading code… ${loading.read} of ${loading.total} files` : 'Laying out…'}
      </div>
    );
  } else {
    body = (
      <Canvas
        graph={graph}
        positions={positions}
        selected={selected}
        onSelect={onSelect}
        onMoved={save}
        onRelayout={() => void relayout()}
        fitKey={`test|${source?.id}|${current.id}`}
        reveal={reveal}
        vocabulary={vocabulary}
        tools={
          <>
            <Divider />
            <ToolButton label={settings.guesses ? 'Do not follow guessed calls' : 'Follow guessed calls too'} active={settings.guesses} onClick={() => settings.toggle('guesses')}>
              <Shuffle size={15} />
            </ToolButton>
          </>
        }
        status={
          <span data-testid="test-status">
            {reach.files.length} {reach.files.length === 1 ? 'file' : 'files'} · {reach.depth.size} {reach.depth.size === 1 ? 'member' : 'members'}
            {reach.fakes.length > 0 && ` · ${reach.fakes.length} faked`}
            {reach.data.length > 0 && ` · ${reach.data.length} ${reach.data.length === 1 ? 'entity' : 'entities'}`}
            {reach.truncated && ' · stopped early, there is more'}
          </span>
        }
      />
    );
  }

  return (
    <div className="@container flex h-full">
      {map && tests.length > 0 && <TestList map={map} current={current} />}
      <div className="flex min-w-0 flex-1 flex-col">
        {tests.length > 0 && (
          <label className="flex flex-none items-center gap-2 border-b border-border bg-panel px-2 py-1.5 @min-[44rem]:hidden">
            <FlaskConical size={14} className="flex-none text-faint" />
            <select
              value={current?.id ?? ''}
              onChange={(e) => showTest(e.target.value)}
              aria-label="Test"
              className="h-8 min-w-0 flex-1 rounded-md border border-border bg-bg px-2 text-[16px] text-text md:text-[12.5px]"
            >
              {groups
                .flatMap((g) => g.classes)
                .map((c) => (
                  <optgroup key={c.type} label={typeName(c.type)}>
                    {c.tests.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
            </select>
          </label>
        )}
        <div className="min-h-0 flex-1">{body}</div>
      </div>
    </div>
  );
}
